// @flow

/**
 * Enhancer to add the following functionality…
 *
 * 1. Snapshotting of initial store state (enhancer must be placed before all other enhancers in order to get default
 *    stated before other enhancers do things like restore state from local storage.
 * 2. Add reducer to store `isPersonalised` flag in state (under the configured `moduleStatePath`).
 * 3. Adds middleware to compare changes to state after reducers have returned the new state to identify any
 *    personalisations, and if so, trigger the `isPersonalised` action, setting the `isPersonalised` flag in state.
 * 4. Scans the store after setup for any personal changes from the default state, and if so, trigger the
 *    `isPersonalised` action, setting the `isPersonalised` flag in state.
 */

import * as Immutable              from 'immutable';
import invariant                   from 'invariant';
import * as Redux                  from "redux";
import applyMiddleware             from 'redux/lib/applyMiddleware';
import type { Action }             from '../../../redux/redux.types';
import type {
  PublicBranch,
  UserConfig,
}                                  from '../../config';
import {
  configure,
  getConfig,
}                                  from '../../config';
import clearPersonalContent        from '../actions/clearPersonalContent';
import isPersonalised              from '../actions/isPersonalised';
import personalContentMiddleware   from '../middleware/personalContentMiddleware';
import compareUpdatedStateWithBase from '../utils/compareUpdatedStateWithBase';
import preparePublicPathTree       from '../utils/preparePublicPathTree';


type StoreCreator<State, Action> = (reducer: Redux.Reducer<State, Action>, preloadedState: State) => Redux.Store<State, Action>;


// -----------------------------------------------------------------------------------------------------------------
// Enhancer
// -----------------------------------------------------------------------------------------------------------------

const personalContentEnhancer = <State: Immutable.Map<string, *>>(config: $Exact<UserConfig>): Redux.StoreEnhancer<State, Action<>> => {

  configure(config);

  return (nextCreateStore): StoreCreator<State, Action<>> => {

    return (reducer: Redux.Reducer<State, Action<>>, initialState: State) => {

      // Expect this enhancer to be called before any others enhancers modify the initialState.
      invariant(initialState.equals(Immutable.Map()), 'Expected initialState to be unmodified');

      const initAction: Action<$Supertype<'@@INIT'>> = {type: '@@INIT'};

      initialState = reducer(undefined, initAction);

      configure({baseState: initialState});

      const monitoredReducer = (state, action) => {

        switch (action.type) {

          case String(clearPersonalContent):

            // Store public branches of state
            const statesByBranch: Array<{ branch: PublicBranch, state: State }> = getConfig().publicBranches.reduce(
              (statesByPath, branch) => statesByPath.concat({
                branch,
                state: state && state.getIn(branch),
              }),
              // Initial statesByBranch
              [],
            );

            // Reset state to initial values
            let updatedState: State = reducer(undefined, {type: '@@INIT'});

            updatedState =
              statesByBranch.reduce(
                (updatedState, {branch, state}): State => updatedState
                  // $FlowFixMe As branch has an undefined length & getIn is limited length
                  && updatedState.setIn(branch, state),
                updatedState,
              );

            return updatedState;

          default:

            return reducer(state, action);

        }

      };

      const store: Redux.Store<State, Action<>> = applyMiddleware(personalContentMiddleware)(nextCreateStore)(
        monitoredReducer,
        initialState,
      );


      const changes = compareUpdatedStateWithBase(store.getState(), getConfig().baseState, preparePublicPathTree());

      if (changes.length) {
        store.dispatch(isPersonalised({causedBy: changes, config: getConfig()}));
      }

      return store;

    };
  };
};


// -----------------------------------------------------------------------------------------------------------------
// Exports
// -----------------------------------------------------------------------------------------------------------------

export default personalContentEnhancer;



// WEBPACK FOOTER //
// ./src/modules/personalContent/store/enhancer/personalContentEnhancer.js